/**
 * CallManager.tsx
 *
 * Gestor global de llamadas — montado UNA sola vez en el root de la app
 * (junto al Router, fuera de ChatWindow/Sidebar).
 *
 * Funciona igual que WhatsApp / Line:
 *  • Escucha llamadas entrantes SIEMPRE, sin importar en qué pantalla esté el usuario.
 *  • Muestra un overlay de llamada entrante con sonido y vibración.
 *  • Soporta llamadas 1-a-1 (VideoCall) y grupales (GroupCall).
 *  • Notifica con la Notifications API si la app está en segundo plano.
 *  • Rechazar la llamada limpia el documento en Firestore.
 *
 * ── Uso ──
 *  En tu App.tsx / main router:
 *
 *    import { CallManager } from './features/chat/CallManager';
 *
 *    function App() {
 *      const { currentUser } = useAuth();   // tu hook de auth
 *      return (
 *        <>
 *          {currentUser && <CallManager currentUser={currentUser} />}
 *          <RouterOutlet />
 *        </>
 *      );
 *    }
 *
 * ── Firestore schema esperado ──
 *  /calls/{callId}
 *    callerUid        string
 *    callerName       string
 *    callerPhoto      string | null
 *    receiverUid      string          (llamadas 1-a-1)
 *    groupId          string | null   (llamadas grupales)
 *    groupName        string | null
 *    callType         'audio' | 'video'
 *    status           'ringing' | 'accepted' | 'rejected' | 'ended' | 'missed'
 *    isGroup          boolean
 *    members          string[]        (grupales: lista de UIDs)
 *    memberProfiles   object          (grupales: { [uid]: { displayName, photoURL } })
 *    createdAt        serverTimestamp
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { db } from '../../services/firebase';
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { Phone, PhoneOff, Video, PhoneMissed } from 'lucide-react';
import { VideoCall } from './VideoCall';
import { GroupCall } from './GroupCall';
import './callmanager.css';

// ── Ringtone ──────────────────────────────────────────────────────────────────
// Genera un tono sintético con Web Audio API para no depender de assets
// Tono para llamada ENTRANTE — con vibración
const createRingtone = (): (() => void) => {
  let ctx: AudioContext | null = null;
  let interval: any = null;
  let stopped = false;

  const playBell = () => {
    if (stopped) return;
    try {
      if (!ctx) ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.4);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.45);
    } catch (e) {}
  };

  playBell();
  interval = setInterval(playBell, 1800);
  if ('vibrate' in navigator) navigator.vibrate([400, 200, 400, 200, 400]);

  return () => {
    stopped = true;
    clearInterval(interval);
    if ('vibrate' in navigator) navigator.vibrate(0);
    ctx?.close().catch(() => {});
  };
};

// Tono para llamada SALIENTE — sin vibración, tono suave de "llamando"
const createOutgoingTone = (): (() => void) => {
  let ctx: AudioContext | null = null;
  let interval: any = null;
  let stopped = false;

  const playTone = () => {
    if (stopped) return;
    try {
      if (!ctx) ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.8);
    } catch (e) {}
  };

  playTone();
  interval = setInterval(playTone, 2000);

  return () => {
    stopped = true;
    clearInterval(interval);
    ctx?.close().catch(() => {});
  };
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface IncomingCall {
  callId: string;
  callerUid: string;
  callerName: string;
  callerPhoto?: string;
  callType: 'audio' | 'video';
  isGroup: boolean;
  groupId?: string;
  groupName?: string;
  members?: string[];
  memberProfiles?: Record<string, { displayName: string; photoURL?: string }>;
  isOutgoing?: boolean;   // true cuando soy el caller
  receiverUid?: string;   // UID del receptor (solo en llamadas 1-a-1 salientes)
  receiverName?: string;
  receiverPhoto?: string;
}

interface Props {
  currentUser: any;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const CallManager = ({ currentUser }: Props) => {
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [activeCall, setActiveCall] = useState<IncomingCall | null>(null);
  const [callState, setCallState] = useState<'ringing' | 'active' | null>(null);
  const [missedCalls, setMissedCalls] = useState<IncomingCall[]>([]);
  const [showMissed, setShowMissed] = useState(false);

  const [outgoingCall, setOutgoingCall] = useState<{
    callId: string;
    receiverUid?: string;
    receiverName: string;
    receiverPhoto?: string;
    callType: 'audio' | 'video';
    isGroup: boolean;
    groupName?: string;
  } | null>(null);

  // Wire module-level setter so startCall() can trigger outgoing UI
  useEffect(() => {
    _outgoingCallSetter.set = setOutgoingCall;
    return () => { _outgoingCallSetter.set = () => {}; };
  }, []);

  const stopRingtoneRef = useRef<(() => void) | null>(null);
  const stopOutgoingToneRef = useRef<(() => void) | null>(null);
  const autoRejectTimerRef = useRef<any>(null);
  const processedCallIds = useRef<Set<string>>(new Set());

  // ── Request notification permission on mount ──
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // ── Notify via Web Notifications API ──
  const sendNotification = useCallback((call: IncomingCall) => {
    if (document.visibilityState === 'visible') return; // Only when in background
    if ('Notification' in window && Notification.permission === 'granted') {
      const icon = call.callerPhoto || undefined;
      const title = call.isGroup
        ? `📞 Llamada grupal: ${call.groupName || 'Grupo'}`
        : `${call.callType === 'video' ? '📹' : '📞'} ${call.callerName} te está llamando`;
      const n = new Notification(title, {
        body: 'Toca para responder',
        icon,
        tag: call.callId,
        requireInteraction: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
    }
  }, []);

  // ── Stop ringtone helper ──
  const stopRingtone = useCallback(() => {
    stopRingtoneRef.current?.();
    stopRingtoneRef.current = null;
    clearTimeout(autoRejectTimerRef.current);
  }, []);

  // ── Accept call ──
  const acceptCall = useCallback(async (call: IncomingCall) => {
    stopRingtone();
    try {
      await updateDoc(doc(db, 'calls', call.callId), { status: 'accepted' });
    } catch (e) {}
    setIncoming(null);
    setActiveCall(call);
    setCallState('active');
  }, [stopRingtone]);

  // ── Reject call ──
  const rejectCall = useCallback(async (call: IncomingCall) => {
    stopRingtone();
    try {
      await updateDoc(doc(db, 'calls', call.callId), {
        status: 'rejected',
        endedAt: serverTimestamp(),
      });
    } catch (e) {}
    setIncoming(null);
  }, [stopRingtone]);

  // ── End active call ──
  const endCall = useCallback(async () => {
    if (activeCall) {
      try {
        await updateDoc(doc(db, 'calls', activeCall.callId), {
          status: 'ended',
          endedAt: serverTimestamp(),
        });
      } catch (e) {}
    }
    setActiveCall(null);
    setCallState(null);
  }, [activeCall]);

  // ── Global incoming call listener ──
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Listen on /calls where receiverUid == me AND status == 'ringing'
    const q = query(
      collection(db, 'calls'),
      where('receiverUid', '==', currentUser.uid),
      where('status', '==', 'ringing'),
    );

    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        const data = change.doc.data();
        const callId = change.doc.id;

        if (change.type === 'added' && !processedCallIds.current.has(callId)) {
          // Ignore very old calls (> 60s) in case of late delivery
          const createdAt = data.createdAt?.toDate?.() || new Date(0);
          const ageMs = Date.now() - createdAt.getTime();
          if (ageMs > 60_000) return;

          processedCallIds.current.add(callId);

          const call: IncomingCall = {
            callId,
            callerUid: data.callerUid,
            callerName: data.callerName,
            callerPhoto: data.callerPhoto,
            callType: data.callType,
            isGroup: data.isGroup || false,
            groupId: data.groupId,
            groupName: data.groupName,
            members: data.members,
            memberProfiles: data.memberProfiles,
          };

          // Don't ring if already on a call
          if (callState === 'active') {
            // Auto-reject busy
            updateDoc(doc(db, 'calls', callId), { status: 'busy' }).catch(() => {});
            return;
          }

          setIncoming(call);
          sendNotification(call);

          // Start ringtone
          stopRingtoneRef.current = createRingtone();

          // Auto-reject after 40 seconds (missed call)
          autoRejectTimerRef.current = setTimeout(() => {
            updateDoc(doc(db, 'calls', callId), {
              status: 'missed',
              endedAt: serverTimestamp(),
            }).catch(() => {});
            stopRingtone();
            setIncoming(null);
            setMissedCalls(prev => [...prev, call]);
            setShowMissed(true);
          }, 40_000);
        }

        if (change.type === 'removed' || (change.type === 'modified' && data.status !== 'ringing')) {
          // Caller hung up before we answered
          if (incoming?.callId === callId && data.status !== 'accepted') {
            stopRingtone();
            setIncoming(null);
            if (data.status === 'ended' || data.status === 'missed') {
              setMissedCalls(prev => {
                const call: IncomingCall = {
                  callId,
                  callerUid: data.callerUid,
                  callerName: data.callerName,
                  callerPhoto: data.callerPhoto,
                  callType: data.callType,
                  isGroup: data.isGroup || false,
                };
                if (prev.find(c => c.callId === callId)) return prev;
                return [...prev, call];
              });
              setShowMissed(true);
            }
          }
        }
      });
    });

    return () => {
      unsub();
      stopRingtone();
    };
  }, [currentUser?.uid, callState, incoming, sendNotification, stopRingtone]);

  // ── Also listen for group calls where I'm a member ──
  useEffect(() => {
    if (!currentUser?.uid) return;

    const q = query(
      collection(db, 'calls'),
      where('members', 'array-contains', currentUser.uid),
      where('isGroup', '==', true),
      where('status', '==', 'ringing'),
    );

    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        const data = change.doc.data();
        const callId = change.doc.id;

        if (
          change.type === 'added' &&
          data.callerUid !== currentUser.uid &&
          !processedCallIds.current.has(callId)
        ) {
          const createdAt = data.createdAt?.toDate?.() || new Date(0);
          const ageMs = Date.now() - createdAt.getTime();
          if (ageMs > 60_000) return;

          processedCallIds.current.add(callId);

          const call: IncomingCall = {
            callId,
            callerUid: data.callerUid,
            callerName: data.callerName,
            callerPhoto: data.callerPhoto,
            callType: data.callType,
            isGroup: true,
            groupId: data.groupId,
            groupName: data.groupName,
            members: data.members,
            memberProfiles: data.memberProfiles,
          };

          if (callState === 'active') {
            updateDoc(doc(db, 'calls', callId), { status: 'busy' }).catch(() => {});
            return;
          }

          setIncoming(call);
          sendNotification(call);
          stopRingtoneRef.current = createRingtone();

          autoRejectTimerRef.current = setTimeout(() => {
            updateDoc(doc(db, 'calls', callId), {
              status: 'missed',
              endedAt: serverTimestamp(),
            }).catch(() => {});
            stopRingtone();
            setIncoming(null);
            setMissedCalls(prev => [...prev, call]);
            setShowMissed(true);
          }, 40_000);
        }
      });
    });

    return () => unsub();
  }, [currentUser?.uid, callState, sendNotification, stopRingtone]);

  // ── Outgoing call listener — muestra "Llamando..." al caller ──
  useEffect(() => {
    if (!outgoingCall) return;

    // Start outgoing tone (sin vibración)
    stopOutgoingToneRef.current = createOutgoingTone();

    const unsub = onSnapshot(doc(db, 'calls', outgoingCall.callId), (snap) => {
      if (!snap.exists()) {
        stopOutgoingToneRef.current?.();
        setOutgoingCall(null);
        return;
      }
      const status = snap.data()?.status;
      if (status === 'accepted') {
        // Receiver accepted — stop tone, open call screen
        stopOutgoingToneRef.current?.();
        const data = snap.data()!;
        setActiveCall({
          callId: outgoingCall.callId,
          callerUid: currentUser.uid,
          callerName: currentUser.displayName || 'Tú',
          callerPhoto: currentUser.photoURL,
          callType: outgoingCall.callType,
          isGroup: outgoingCall.isGroup,
          groupId: data.groupId,
          groupName: outgoingCall.groupName,
          members: data.members,
          memberProfiles: data.memberProfiles,
          isOutgoing: true,
          receiverUid: outgoingCall.receiverUid,
          receiverName: outgoingCall.receiverName,
          receiverPhoto: outgoingCall.receiverPhoto,
        });
        setCallState('active');
        setOutgoingCall(null);
      } else if (['rejected', 'ended', 'missed', 'busy'].includes(status)) {
        stopOutgoingToneRef.current?.();
        setOutgoingCall(null);
      }
    });

    // Auto-cancel after 40s if no answer
    const timer = setTimeout(async () => {
      stopOutgoingToneRef.current?.();
      try {
        await updateDoc(doc(db, 'calls', outgoingCall.callId), {
          status: 'missed', endedAt: serverTimestamp(),
        });
      } catch (e) {}
      setOutgoingCall(null);
    }, 40_000);

    return () => {
      unsub();
      clearTimeout(timer);
      stopOutgoingToneRef.current?.();
    };
  }, [outgoingCall?.callId]);

  const getInitials = (name: string) => (name || '?').slice(0, 2).toUpperCase();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Outgoing call overlay (caller side) ── */}
      {outgoingCall && (
        <div className="cm-overlay">
          <div className="cm-card">
            <div className="cm-rings">
              <div className="cm-ring cm-ring--1" />
              <div className="cm-ring cm-ring--2" />
              <div className="cm-ring cm-ring--3" />
              <div className="cm-avatar">
                {outgoingCall.receiverPhoto
                  ? <img src={outgoingCall.receiverPhoto} alt="" />
                  : <span>{getInitials(outgoingCall.receiverName)}</span>}
              </div>
            </div>
            <div className="cm-info">
              <div className="cm-call-type">
                {outgoingCall.callType === 'video' ? '📹 Videollamada' : '📞 Llamada de voz'}
              </div>
              <div className="cm-caller-name">
                {outgoingCall.isGroup ? outgoingCall.groupName : outgoingCall.receiverName}
              </div>
              <div className="cm-ringing-dots"><span /><span /><span /></div>
              <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: 4 }}>Llamando...</div>
            </div>
            <div className="cm-actions" style={{ justifyContent: 'center' }}>
              <div className="cm-action-wrap">
                <button
                  className="cm-btn cm-btn--reject"
                  onClick={async () => {
                    stopOutgoingToneRef.current?.();
                    try {
                      await updateDoc(doc(db, 'calls', outgoingCall.callId), {
                        status: 'ended', endedAt: serverTimestamp(),
                      });
                    } catch (e) {}
                    setOutgoingCall(null);
                  }}
                  aria-label="Cancelar"
                >
                  <PhoneOff size={26} />
                </button>
                <span className="cm-btn-label">Cancelar</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Incoming call overlay ── */}
      {incoming && (
        <div className="cm-overlay">
          <div className="cm-card">
            {/* Animated rings */}
            <div className="cm-rings">
              <div className="cm-ring cm-ring--1" />
              <div className="cm-ring cm-ring--2" />
              <div className="cm-ring cm-ring--3" />
              <div className="cm-avatar">
                {incoming.callerPhoto
                  ? <img src={incoming.callerPhoto} alt="" />
                  : <span>{getInitials(incoming.callerName)}</span>}
              </div>
            </div>

            <div className="cm-info">
              <div className="cm-call-type">
                {incoming.isGroup
                  ? `📞 Llamada grupal${incoming.callType === 'video' ? ' de video' : ''}`
                  : incoming.callType === 'video' ? '📹 Videollamada entrante' : '📞 Llamada de voz'}
              </div>
              <div className="cm-caller-name">
                {incoming.isGroup ? (incoming.groupName || 'Grupo') : incoming.callerName}
              </div>
              {incoming.isGroup && (
                <div className="cm-caller-sub">{incoming.callerName} inició la llamada</div>
              )}
              <div className="cm-ringing-dots">
                <span /><span /><span />
              </div>
            </div>

            <div className="cm-actions">
              <div className="cm-action-wrap">
                <button
                  className="cm-btn cm-btn--reject"
                  onClick={() => rejectCall(incoming)}
                  aria-label="Rechazar"
                >
                  <PhoneOff size={26} />
                </button>
                <span className="cm-btn-label">Rechazar</span>
              </div>
              <div className="cm-action-wrap">
                <button
                  className="cm-btn cm-btn--accept"
                  onClick={() => acceptCall(incoming)}
                  aria-label="Aceptar"
                >
                  {incoming.callType === 'video' ? <Video size={26} /> : <Phone size={26} />}
                </button>
                <span className="cm-btn-label">Aceptar</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Missed calls toast ── */}
      {showMissed && missedCalls.length > 0 && (
        <div className="cm-missed-toast" onClick={() => setShowMissed(false)}>
          <PhoneMissed size={16} />
          <span>
            {missedCalls.length === 1
              ? `Llamada perdida de ${missedCalls[missedCalls.length - 1].callerName}`
              : `${missedCalls.length} llamadas perdidas`}
          </span>
          <button onClick={() => { setMissedCalls([]); setShowMissed(false); }}>×</button>
        </div>
      )}

      {/* ── Active 1-to-1 call ── */}
      {activeCall && !activeCall.isGroup && (
        <VideoCall
          currentUser={currentUser}
          remoteUser={activeCall.isOutgoing ? {
            uid: activeCall.receiverUid || '',
            displayName: activeCall.receiverName || '',
            photoURL: activeCall.receiverPhoto,
          } : {
            uid: activeCall.callerUid,
            displayName: activeCall.callerName,
            photoURL: activeCall.callerPhoto,
          }}
          callType={activeCall.callType}
          callId={activeCall.callId}
          isIncoming={!activeCall.isOutgoing}
          onEnd={endCall}
        />
      )}

      {/* ── Active group call ── */}
      {activeCall && activeCall.isGroup && activeCall.groupId && (
        <GroupCall
          currentUser={currentUser}
          groupId={activeCall.groupId}
          groupName={activeCall.groupName || 'Llamada grupal'}
          members={
            Object.entries(activeCall.memberProfiles || {}).map(([uid, p]: [string, any]) => ({
              uid,
              displayName: p.displayName,
              photoURL: p.photoURL,
            }))
          }
          callType={activeCall.callType}
          onEnd={endCall}
        />
      )}
    </>
  );
};

/**
 * ── Utilidad: iniciar una llamada saliente ──────────────────────────────────
 *
 * Úsala desde ChatWindow (o cualquier botón de llamar) para crear
 * el documento en /calls y hacer sonar el teléfono del receptor.
 *
 * Ejemplo:
 *   import { startCall } from './CallManager';
 *   await startCall({ currentUser, remoteUser, callType: 'video', chatId });
 */
// Module-level ref — allows startCall() to set outgoing call UI
export const _outgoingCallSetter = { set: (_v: any) => {} };

export const startCall = async ({
  currentUser,
  remoteUser,
  callType,
  chatId,
}: {
  currentUser: any;
  remoteUser: { uid: string; displayName: string; photoURL?: string };
  callType: 'audio' | 'video';
  chatId: string;
}): Promise<string> => {
  const callRef = doc(collection(db, 'calls'));
  await setDoc(callRef, {
    callerUid: currentUser.uid,
    callerName: currentUser.displayName || 'Usuario',
    callerPhoto: currentUser.photoURL || null,
    receiverUid: remoteUser.uid,
    callType,
    isGroup: false,
    chatId,
    status: 'ringing',
    createdAt: serverTimestamp(),
  });
  _outgoingCallSetter.set({
    callId: callRef.id,
    receiverUid: remoteUser.uid,
    receiverName: remoteUser.displayName,
    receiverPhoto: remoteUser.photoURL,
    callType,
    isGroup: false,
  });
  return callRef.id;
};

/**
 * ── Utilidad: iniciar una llamada grupal saliente ──────────────────────────
 *
 * Ejemplo:
 *   import { startGroupCall } from './CallManager';
 *   await startGroupCall({ currentUser, groupId, groupName, members, memberProfiles, callType });
 */
export const startGroupCall = async ({
  currentUser,
  groupId,
  groupName,
  members,
  memberProfiles,
  callType,
}: {
  currentUser: any;
  groupId: string;
  groupName: string;
  members: string[];
  memberProfiles: Record<string, { displayName: string; photoURL?: string }>;
  callType: 'audio' | 'video';
}): Promise<string> => {
  const callRef = doc(collection(db, 'calls'));
  await setDoc(callRef, {
    callerUid: currentUser.uid,
    callerName: currentUser.displayName || 'Usuario',
    callerPhoto: currentUser.photoURL || null,
    groupId,
    groupName,
    members,
    memberProfiles,
    callType,
    isGroup: true,
    // receiverUid is not needed for group — listener uses array-contains on members
    receiverUid: '__group__',
    status: 'ringing',
    createdAt: serverTimestamp(),
  });
  return callRef.id;
};